import { createWithEqualityFn } from 'zustand/traditional';
import { shallow } from 'zustand/shallow';
import { immer } from 'zustand/middleware/immer';
import {
  OfferCompare,
  OfferCompareState,
  OfferComparisonItems,
  OfferStep,
} from '../../../shared/types/offers';
import { ExternalPricingSheetItem } from '../../../shared/types/pricingSheetItems';

type State = {
  activeStep: OfferStep;
  compare: OfferCompareState;
};

type Actions = {
  setActiveStep: (step: OfferStep) => void;
  setCompareState: (
    key: keyof OfferCompareState,
    state: Partial<
      Omit<OfferCompare, 'items'> & {
        items: ExternalPricingSheetItem[];
      }
    > | null,
  ) => void;
  clearCompareState: () => void;
};

const defaultState: OfferCompare = {
  id: undefined,
  items: null,
  name: '',
  selectedOption: undefined,
  tooltipTitle: '',
};

const INIT_COMPARE_STATE: OfferCompareState = {
  comparisonSheet: {
    ...defaultState,
    tooltipTitle:
      'Lataa hinnasto vertailuun MIRA:sta. Vertailuhinnat latautuvat taulukon VRT-sarakkeisiin',
  },
  contractSheet: {
    ...defaultState,
    tooltipTitle:
      'Taulukkoon lautautuu automaattisesti asiakkaan MIRA:ssa asetettu sopimushinnasto. Voit kuitenkin vaihtaa hinnastoa halutessasi. Sopimushinnat latautuvat taulukon Sopimushinta-sarakkeisiin',
  },
};

export const useOfferStore = createWithEqualityFn(
  immer<State & Actions>((set) => ({
    activeStep: OfferStep.Details,
    compare: INIT_COMPARE_STATE,
    setActiveStep: (activeStep) => set(() => ({ activeStep })),
    setCompareState: (key, updatedState) =>
      set((state) => {
        if (updatedState === null) {
          // reset state
          state.compare[key] = defaultState;
          return;
        }
        state.compare[key] = {
          ...state.compare[key],
          ...updatedState,
          items: updatedState.items
            ? updatedState.items.reduce(
                (
                  acc,
                  { dayPrice, monthPrice, item: { catClass } },
                ) => ({
                  ...acc,
                  [catClass]: {
                    dayPrice: dayPrice
                      ? Math.round(Number(dayPrice) * 10000)
                      : null,
                    monthPrice: monthPrice
                      ? Math.round(Number(monthPrice) * 10000)
                      : null,
                  },
                }),
                {} as OfferComparisonItems,
              )
            : state.compare[key].items,
        };
      }),
    clearCompareState: () =>
      set(() => ({ compare: INIT_COMPARE_STATE })),
  })),
  shallow,
);
